import React, { useState, useEffect, useRef } from 'react';
import { FaTrash, FaSpinner, FaChevronDown } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import { Plus } from 'lucide-react';
import { FilterColumnData, FilterOperator } from '../../../../api/services/ai-chat-api-service';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import SearchableDropdown, { SearchableDropdownOption } from '../../../atoms/Custom/SearchableDropdown';

interface FilterRow {
	id: string;
	column: string;
	operator: string;
	value: string;
	selectedValues: string[];
	isExisting: boolean;
	isDeleted?: boolean;
}

interface FilterDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	availableColumns: string[];
	filterDataMap: Record<string, FilterColumnData>;
	existingFilters: FilterRow[];
	onApply: (filters: FilterRow[]) => void;
	isLoading?: boolean;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
	isOpen,
	onClose,
	availableColumns,
	filterDataMap,
	existingFilters,
	onApply,
	isLoading = false,
}) => {
	const [filterRows, setFilterRows] = useState<FilterRow[]>([]);
	const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
	const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
	const [showDatePickers, setShowDatePickers] = useState<Record<string, boolean>>({});
	const [selectedDates, setSelectedDates] = useState<Record<string, Date | null>>({});
	const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

	useEffect(() => {
		if (isOpen) {
			setFilterRows(existingFilters.length > 0 ? [...existingFilters] : []);
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen, existingFilters]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			Object.keys(openDropdowns).forEach(filterId => {
				const dropdownRef = dropdownRefs.current[filterId];
				if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
					setOpenDropdowns(prev => ({ ...prev, [filterId]: false }));
					setShowDatePickers(prev => ({ ...prev, [filterId]: false }));
				}
			});
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
	}, [isOpen, openDropdowns]);

	const formatFieldName = (fieldName: string): string => {
		return fieldName
			.toLowerCase()
			.split('_')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	const getOperatorsForColumn = (column: string): FilterOperator[] => {
		const columnData = filterDataMap[column.toLowerCase()];
		if (columnData?.operators && columnData.operators.length > 0) {
			return columnData.operators;
		}
		return [
			{ value: '=', label: 'equals' },
			{ value: '!=', label: 'not equals' },
			{ value: '>', label: 'greater than' },
			{ value: '<', label: 'less than' },
			{ value: '>=', label: 'greater than or equal to' },
			{ value: '<=', label: 'less than or equal to' },
			{ value: 'LIKE', label: 'contains' },
			{ value: 'IN', label: 'is one of' },
			{ value: 'NOT IN', label: 'is not one of' },
		];
	};

	const handleAddFilter = () => {
		const newFilter: FilterRow = {
			id: `new_${Date.now()}`,
			column: '',
			operator: '=',
			value: '',
			selectedValues: [],
			isExisting: false,
		};
		setFilterRows([...filterRows, newFilter]);
	};

	const handleRemoveFilter = (id: string) => {
		const updatedFilters = filterRows.map((filter) => {
			if (filter.id === id) {
				if (filter.isExisting) {
					return { ...filter, isDeleted: true };
				}
				return null;
			}
			return filter;
		}).filter(Boolean) as FilterRow[];
		setFilterRows(updatedFilters);
	};

	const handleColumnChange = (id: string, column: string) => {
		const updatedFilters = filterRows.map((filter) => {
			if (filter.id === id) {
				const operators = getOperatorsForColumn(column);
				return {
					...filter,
					column,
					operator: operators[0]?.value || '=',
					value: '',
					selectedValues: [],
				};
			}
			return filter;
		});
		setFilterRows(updatedFilters);
	};

	const handleOperatorChange = (id: string, operator: string) => {
		const updatedFilters = filterRows.map((filter) => {
			if (filter.id === id) {
				return { ...filter, operator };
			}
			return filter;
		});
		setFilterRows(updatedFilters);
	};

	const handleValueChange = (id: string, value: string) => {
		const updatedFilters = filterRows.map((filter) => {
			if (filter.id === id) {
				return { ...filter, value };
			}
			return filter;
		});
		setFilterRows(updatedFilters);
	};

	const handleDropdownValueToggle = (id: string, value: string) => {
		const updatedFilters = filterRows.map((filter) => {
			if (filter.id === id) {
				const selectedValues = filter.selectedValues.includes(value)
					? filter.selectedValues.filter((v) => v !== value)
					: [...filter.selectedValues, value];
				return { ...filter, selectedValues };
			}
			return filter;
		});
		setFilterRows(updatedFilters);
	};

	const toggleDropdown = (id: string) => {
		setOpenDropdowns((prev) => ({
			...prev,
			[id]: !prev[id],
		}));
		setShowDatePickers((prev) => ({
			...prev,
			[id]: false,
		}));
	};

	const toggleDatePicker = (id: string) => {
		setShowDatePickers((prev) => ({
			...prev,
			[id]: !prev[id],
		}));
		setOpenDropdowns((prev) => ({
			...prev,
			[id]: false,
		}));
	};

	const handleSearchChange = (id: string, searchTerm: string) => {
		setSearchTerms((prev) => ({
			...prev,
			[id]: searchTerm,
		}));
	};


	const handleDateChange = (id: string, date: Date | null) => {
		setSelectedDates((prev) => ({
			...prev,
			[id]: date,
		}));
		
		if (date) {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			const formattedDate = `${year}-${month}-${day}`;
			handleValueChange(id, formattedDate);
			setShowDatePickers((prev) => ({
				...prev,
				[id]: false,
			}));
		}
	};

	const handleApply = () => {
		const validFilters = filterRows.filter((f) => {
			if (f.isDeleted) return true;
			if (!f.column) return false;
			const columnData = filterDataMap[f.column.toLowerCase()];
			const isDropdown = columnData?.suggestion?.toLowerCase() === 'dropdown';
			return isDropdown ? f.selectedValues.length > 0 : f.value.trim() !== '';
		});
		onApply(validFilters);
	};

	const renderCompactValueInput = (filter: FilterRow) => {
		const columnData = filterDataMap[filter.column?.toLowerCase()];
		const suggestion = columnData?.suggestion?.toLowerCase();
		const isOpen = openDropdowns[filter.id];
		const showDatePicker = showDatePickers[filter.id];
		const searchTerm = searchTerms[filter.id] || '';


		// Common input styles for consistent sizing
		const inputStyles = "w-full px-2 py-1.5 text-xs text-primary-text border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-primary min-h-[32px]";
		const dropdownStyles = "w-full px-2 py-1.5 text-xs text-primary-text border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer bg-white min-h-[32px] flex items-center justify-between";

		if (suggestion === 'dropdown' && columnData?.values && columnData.values.length > 0) {
			const filteredValues = columnData.values.filter(value =>
				value.toLowerCase().includes(searchTerm.toLowerCase())
			);

			return (
				<div className="relative" ref={el => dropdownRefs.current[filter.id] = el}>
					<div
						onClick={() => toggleDropdown(filter.id)}
						className={dropdownStyles}
					>
						{filter.selectedValues.length === 0 ? (
							<span className="text-gray-400">Value...</span>
						) : (
							<span className="truncate">
								{filter.selectedValues.length === 1
									? filter.selectedValues[0]
									: `${filter.selectedValues.length} items`}
							</span>
						)}
						<FaChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
					</div>

					{isOpen && (
						<div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-hidden">
							{/* Search input */}
							<div className="p-2 border-b border-gray-200">
								<input
									type="text"
									value={searchTerm}
									onChange={(e) => handleSearchChange(filter.id, e.target.value)}
									placeholder="Search..."
									className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary"
									onClick={(e) => e.stopPropagation()}
								/>
							</div>
							
							{/* Options list */}
							<div className="max-h-32 overflow-y-auto">
								{filteredValues.length > 0 ? (
									filteredValues.map((value) => (
										<div
											key={value}
											onClick={() => handleDropdownValueToggle(filter.id, value)}
											className="px-2 py-1.5 hover:bg-blue-50 cursor-pointer flex items-center space-x-2 text-xs"
										>
											<input
												type="checkbox"
												checked={filter.selectedValues.includes(value)}
												onChange={() => {}}
												className="w-3 h-3 text-primary border-gray-300 rounded focus:ring-primary"
												aria-label={`Select ${value}`}
											/>
											<span className="text-primary-text truncate">{value}</span>
										</div>
									))
								) : (
									<div className="px-2 py-1.5 text-xs text-gray-500 text-center">
										No matching values
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			);
		}

		if (suggestion === 'datepicker') {
			return (
				<div className="relative" ref={el => dropdownRefs.current[filter.id] = el}>
					<div
						onClick={() => toggleDatePicker(filter.id)}
						className={dropdownStyles}
					>
						{filter.value ? (
							<span className="truncate">{filter.value}</span>
						) : (
							<span className="text-gray-400">Select date...</span>
						)}
						<FaChevronDown className={`w-3 h-3 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
					</div>

					{showDatePicker && (
						<div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
							<Calendar
								onChange={(date) => handleDateChange(filter.id, date as Date)}
								value={selectedDates[filter.id] || null}
								className="ai-calendar"
								maxDetail="month"
								minDetail="decade"
								showNavigation={true}
								showNeighboringMonth={false}
								calendarType="gregory"
								locale="en-US"
							/>
						</div>
					)}
				</div>
			);
		}

		if (suggestion === 'enternumber') {
			return (
				<input
					type="number"
					value={filter.value}
					onChange={(e) => handleValueChange(filter.id, e.target.value)}
					placeholder="Number..."
					className={inputStyles}
					disabled={!filter.column}
				/>
			);
		}

		return (
			<input
				type="text"
				value={filter.value}
				onChange={(e) => handleValueChange(filter.id, e.target.value)}
				placeholder="Value..."
				className={inputStyles}
				disabled={!filter.column}
			/>
		);
	};

	const getColumnOptions = (): SearchableDropdownOption[] => {
		return availableColumns.map(col => ({
			value: col,
			label: formatFieldName(col)
		}));
	};

	const getOperatorOptions = (column: string): SearchableDropdownOption[] => {
		return getOperatorsForColumn(column).map(op => ({
			value: op.value,
			label: op.label
		}));
	};


	if (!isOpen) return null;

	const activeFilters = filterRows.filter((f) => !f.isDeleted);
	const hasValidFilters = activeFilters.some((filter) => {
		if (!filter.column) return false;
		const columnData = filterDataMap[filter.column.toLowerCase()];
		const isDropdown = columnData?.suggestion?.toLowerCase() === 'dropdown';
		return isDropdown ? filter.selectedValues.length > 0 : filter.value.trim() !== '';
	});

	return (
		<div className="fixed inset-0 z-50 flex justify-end">
			<div
				className="absolute inset-0 bg-black bg-opacity-50"
				onClick={onClose}
			/>
			<div className="relative w-full md:w-[40rem] h-screen bg-white flex flex-col shadow-lg">
				<div className="pl-8 pr-3 py-4 border-b flex items-center justify-between bg-white flex-shrink-0">
					<div className="flex items-center text-primary-text flex-1 pr-2">
						<h2 className="text-lg font-semibold">Manage Filters</h2>
					</div>
					<button
						onClick={onClose}
						className="p-2 group flex items-center justify-center rounded-full transition-colors active:text-primary text-primary-text hover:bg-secondary-background hover:text-primary flex-shrink-0"
						aria-label="Close filter drawer"
					>
						<FiX className="w-4 h-4" strokeWidth={1.75} />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto p-4">
					{activeFilters.length === 0 && (
						<div className="text-center py-8 text-gray-500">
							<p>No filters added yet. Click "Add Filter" to get started.</p>
						</div>
					)}

					<div className="space-y-1">
						{activeFilters.map((filter, index) => (
							<div
								key={filter.id}
								className="bg-white rounded-lg p-3 transition-colors"
							>
								<div className="flex items-center gap-3">
									{/* Filter Fields - Horizontal Layout */}
									<div className="flex-1 grid grid-cols-3 gap-2">
										{/* Column */}
										<div className="min-w-0">
											<SearchableDropdown
												options={getColumnOptions()}
												value={filter.column}
												onChange={(value) => handleColumnChange(filter.id, value)}
												placeholder="Column..."
												searchPlaceholder="Search columns..."
											/>
										</div>

										{/* Operator */}
										<div className="min-w-0">
											<SearchableDropdown
												options={getOperatorOptions(filter.column)}
												value={filter.operator}
												onChange={(value) => handleOperatorChange(filter.id, value)}
												placeholder="Operator..."
												searchPlaceholder="Search operators..."
												disabled={!filter.column}
											/>
										</div>

										{/* Value */}
										<div className="min-w-0">
											{renderCompactValueInput(filter)}
										</div>
									</div>

									{/* Remove Button */}
									<button
										onClick={() => handleRemoveFilter(filter.id)}
										className="p-2 group flex items-center justify-center rounded-full transition-colors active:text-red-500 text-red-500 hover:bg-red-50 hover:text-red-700 flex-shrink-0"
										title="Remove filter"
									>
										<FaTrash className="w-4 h-4" />
									</button>
								</div>
							</div>
						))}

						{/* Add Filter Button - Always at bottom of list */}
						<div className="ml-3 pt-2 flex justify-start">
							<button
								onClick={handleAddFilter}
								className="px-4 py-2 text-primary border border-primary bg-white rounded-full hover:bg-blue-50 transition-colors text-ui flex items-center space-x-2"
							>
								<Plus className="w-4 h-4" strokeWidth={1.75} />
								<span>Add Filter</span>
							</button>
						</div>
					</div>
				</div>

				<div className="px-4 py-3 border-t bg-white flex-shrink-0">
					<div className="flex items-center justify-end space-x-2">
						<button
							onClick={onClose}
							className="px-4 py-2 text-tertiary-text border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-ui"
						>
							Cancel
						</button>
						<button
							onClick={handleApply}
							disabled={!hasValidFilters || isLoading}
							className="px-6 py-2 bg-primary text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-ui flex items-center justify-center"
						>
							{isLoading ? (
								<>
									<FaSpinner className="w-4 h-4 animate-spin mr-2" />
									Applying...
								</>
							) : (
								'Apply Filters'
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FilterDrawer;

